import { Button, Card, EmptyState, Inline, Stack } from "@genius-tracker/design-system";
import { useLocale } from "@genius-tracker/i18n/react";
import { useQuery } from "@tanstack/react-query";
import { apiGet, ApiError } from "../lib/api.js";

/**
 * Sprint 25 Wave D PR-D3 — Tenant 360° vehicle appearance card.
 *
 * Read-only view of the customer tenant's vehicles plus their
 * operator-chosen icon kind / marker color. Lets the platform
 * owner sanity-check the customer's fleet symbology without
 * impersonating a tenant member.
 *
 * Backed by `GET /v1/admin/tenants/:id/vehicles` (PR-D3 service).
 * The component renders a small inline SVG marker preview for
 * each vehicle so the admin sees exactly what the customer's
 * dispatcher sees on the live map. The preview SVG is duplicated
 * here from `apps/web/src/vehicles/VehicleMarkerPreview.tsx`
 * because the admin app deliberately does not depend on the web
 * app — both surfaces converge on the same icon paths through a
 * monorepo-internal shared package in a future sprint.
 */

// GT-VEHICLE-TYPE-ORDER-01 — mirrors the @genius-tracker/web
// VEHICLE_ICON_KINDS frequency/importance order (set is identical to the
// server CHECK; placeholder silhouettes: suv/pickup/off_road → car;
// dump/tank/crane/cargo trucks → truck; minibus → van;
// bicycle/scooter/tractor → motorcycle).
const VEHICLE_ICON_KINDS = [
  // Light passenger.
  "car",
  "generic",
  "suv",
  "off_road",
  "pickup",
  // Vans / people movers.
  "van",
  "minibus",
  "bus",
  // Heavy goods.
  "truck",
  "trailer",
  "dump_truck",
  "tank_truck",
  "cargo_truck",
  "crane_truck",
  // Industrial / agricultural / special.
  "machine",
  "tractor",
  "asset",
  // Two-wheelers (last).
  "motorcycle",
  "bicycle",
  "scooter",
] as const;
type VehicleIconKind = (typeof VEHICLE_ICON_KINDS)[number];

const VEHICLE_OPERATIONAL_STATUSES = ["active", "idle", "offline"] as const;
type VehicleOperationalStatus = (typeof VEHICLE_OPERATIONAL_STATUSES)[number];

interface AdminVehicleAppearanceRow {
  readonly id: string;
  readonly plate: string;
  readonly displayName: string | null;
  readonly kind: string;
  readonly status: string;
  readonly operationalStatus: string;
  readonly iconKind: string;
  readonly markerColor: string;
  readonly archivedAt: string | null;
  readonly createdAt: string;
}

interface AdminVehicleAppearanceResponse {
  readonly items: readonly AdminVehicleAppearanceRow[];
  readonly total: number;
  readonly capped: boolean;
}

export function TenantVehicleAppearanceCard({ tenantId }: { tenantId: string }) {
  const { t } = useLocale();
  const query = useQuery<AdminVehicleAppearanceResponse, Error>({
    queryKey: ["admin", "tenants", tenantId, "vehicles"],
    queryFn: ({ signal }) =>
      apiGet<AdminVehicleAppearanceResponse>(
        `/v1/admin/tenants/${tenantId}/vehicles`,
        undefined,
        signal,
      ),
    staleTime: 30_000,
  });

  return (
    <Card elevation={1}>
      <Stack gap={3} data-testid="admin-tenant-vehicles-appearance">
        <Inline justify="space-between" align="center" wrap>
          <strong>{t("admin.tenant_360.vehicle_appearance.title")}</strong>
          {query.data && query.data.total > 0 ? (
            <span style={countPillStyle} data-testid="admin-tenant-vehicles-count">
              {t("admin.tenant_360.vehicle_appearance.count", {
                shown: query.data.items.length,
                total: query.data.total,
              })}
            </span>
          ) : null}
        </Inline>
        {query.isLoading ? (
          <span style={mutedSmallStyle}>{t("common.loading")}</span>
        ) : query.isError ? (
          <Stack gap={2}>
            <span
              role="alert"
              data-testid="admin-tenant-vehicles-error"
              style={{ color: "var(--gt-sys-status-danger)" }}
            >
              {errorMessageFor(query.error, t)}
            </span>
            {/* Sprint 25 PR-H3 — retry. Refetch is safe because the
                query is read-only. */}
            <Inline>
              <Button
                variant="secondary"
                onClick={() => void query.refetch()}
                data-testid="admin-tenant-vehicles-retry"
              >
                {t("common.retry")}
              </Button>
            </Inline>
          </Stack>
        ) : query.data && query.data.items.length === 0 ? (
          <EmptyState
            title={t("admin.tenant_360.vehicle_appearance.empty.title")}
            body={t("admin.tenant_360.vehicle_appearance.empty.body")}
          />
        ) : query.data ? (
          <Stack gap={2}>
            {query.data.items.map((row) => (
              <Inline
                key={row.id}
                gap={3}
                align="center"
                wrap
                data-testid="admin-tenant-vehicles-row"
              >
                <VehicleMarker
                  iconKind={normalizeIconKind(row.iconKind)}
                  color={normalizeColor(row.markerColor)}
                  operationalStatus={normalizeOperationalStatus(row.operationalStatus)}
                />
                <Stack gap={0} style={{ minInlineSize: 220, flexGrow: 1 }}>
                  <strong style={{ fontSize: "var(--gt-sys-font-size-sm)" }}>
                    {row.displayName ?? row.plate}
                  </strong>
                  <span style={mutedXsStyle}>
                    {row.displayName ? `${row.plate} · ` : ""}
                    {t(
                      `admin.tenant_360.vehicle_appearance.icon_kind.${normalizeIconKind(
                        row.iconKind,
                      )}` as const,
                    )}{" "}
                    · {row.markerColor}
                  </span>
                  <span style={mutedXsStyle}>
                    {t("admin.tenant_360.vehicle_appearance.row.summary", {
                      kind: row.kind,
                      status: row.status,
                      opStatus: row.operationalStatus,
                    })}
                  </span>
                </Stack>
                {row.archivedAt ? (
                  <span style={archivedBadgeStyle} data-testid="admin-tenant-vehicles-archived">
                    {t("admin.tenant_360.vehicle_appearance.archived")}
                  </span>
                ) : null}
              </Inline>
            ))}
            {query.data.capped ? (
              <span style={mutedXsStyle} data-testid="admin-tenant-vehicles-cap-hint">
                {t("admin.tenant_360.vehicle_appearance.cap", {
                  shown: query.data.items.length,
                  total: query.data.total,
                })}
              </span>
            ) : null}
          </Stack>
        ) : null}
      </Stack>
    </Card>
  );
}

/**
 * Inline SVG marker preview duplicated from
 * apps/web/src/vehicles/VehicleMarkerPreview.tsx so the admin app
 * can render the exact same icon a customer dispatcher sees on the
 * live map. The web app's component is the canonical source; if a
 * shared design-system package is added later, both surfaces will
 * import from there.
 */
function VehicleMarker(props: {
  iconKind: VehicleIconKind;
  color: string;
  operationalStatus: VehicleOperationalStatus | null;
}) {
  const path = ICON_PATHS[props.iconKind] ?? ICON_PATHS.generic;
  const stroke = isDarkColor(props.color) ? "#ffffff" : "#1f2933";
  const ringColor =
    props.operationalStatus === "active"
      ? "#10b981"
      : props.operationalStatus === "idle"
        ? "#f59e0b"
        : props.operationalStatus === "offline"
          ? "#7b8794"
          : "transparent";
  return (
    <svg
      width={32}
      height={32}
      viewBox="0 0 32 32"
      role="img"
      aria-label={props.iconKind}
      data-testid="admin-tenant-vehicle-marker"
      data-icon-kind={props.iconKind}
      data-color={props.color}
      style={{ flexShrink: 0 }}
    >
      <circle cx={16} cy={16} r={14} fill="none" stroke={ringColor} strokeWidth={2} />
      <circle cx={16} cy={16} r={11} fill={props.color} stroke={stroke} strokeWidth={1} />
      <g transform="translate(4 4)">
        <path
          d={path}
          fill="none"
          stroke={stroke}
          strokeWidth={1.5}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </g>
    </svg>
  );
}

const ICON_PATHS: Record<VehicleIconKind, string> = {
  car: "M5 12l1.5-4.5A2 2 0 018.4 6h7.2a2 2 0 011.9 1.5L19 12v6a1 1 0 01-1 1h-1a1 1 0 01-1-1v-1H8v1a1 1 0 01-1 1H6a1 1 0 01-1-1v-6z M7 15h2 M15 15h2",
  truck:
    "M2 7h11v8H2zm11 3h4l3 3v2h-7zM6 17.5a1.5 1.5 0 113 0 1.5 1.5 0 01-3 0zm10 0a1.5 1.5 0 113 0 1.5 1.5 0 01-3 0z",
  van: "M3 7h13v8H3zM16 9h4l2 2v4h-6zM6 17.5a1.5 1.5 0 113 0 1.5 1.5 0 01-3 0zm12 0a1.5 1.5 0 113 0 1.5 1.5 0 01-3 0z",
  trailer: "M2 9h17v6H2zm17 3h3v3h-3z M5 17.5a1.5 1.5 0 113 0 1.5 1.5 0 01-3 0z",
  bus: "M5 5h14v12a2 2 0 01-2 2H7a2 2 0 01-2-2zm0 4h14m-9 0v8M7 18.5a1 1 0 102 0 1 1 0 00-2 0zm8 0a1 1 0 102 0 1 1 0 00-2 0z",
  motorcycle:
    "M5 17.5a2.5 2.5 0 105 0 2.5 2.5 0 00-5 0zm9 0a2.5 2.5 0 105 0 2.5 2.5 0 00-5 0zM7.5 17l5-7h3l1 4M14 8l3 0M9 9h2",
  machine:
    "M3 14h6l2-3h4l3 3v3H3zM5 17.5a1.5 1.5 0 113 0 1.5 1.5 0 01-3 0zm10 0a1.5 1.5 0 113 0 1.5 1.5 0 01-3 0z M11 7h4l2 4",
  asset: "M4 7l8-3 8 3v10l-8 3-8-3zm8-3v16M4 7l8 3 8-3",
  generic: "M12 4l8 4v8l-8 4-8-4V8z",
  // Sprint 29.4.4 — V2 placeholder silhouettes for the admin
  // appearance preview. The customer Live Map uses the V3 owner
  // assets via `@genius-tracker/web` and renders them at full
  // fidelity; the admin card just needs a recognisable preview
  // of the kind, so we re-use the closest existing silhouette.
  suv: "M5 12l1.5-4.5A2 2 0 018.4 6h7.2a2 2 0 011.9 1.5L19 12v6a1 1 0 01-1 1h-1a1 1 0 01-1-1v-1H8v1a1 1 0 01-1 1H6a1 1 0 01-1-1v-6z M7 15h2 M15 15h2",
  pickup:
    "M5 12l1.5-4.5A2 2 0 018.4 6h7.2a2 2 0 011.9 1.5L19 12v6a1 1 0 01-1 1h-1a1 1 0 01-1-1v-1H8v1a1 1 0 01-1 1H6a1 1 0 01-1-1v-6z M7 15h2 M15 15h2",
  dump_truck:
    "M2 7h11v8H2zm11 3h4l3 3v2h-7zM6 17.5a1.5 1.5 0 113 0 1.5 1.5 0 01-3 0zm10 0a1.5 1.5 0 113 0 1.5 1.5 0 01-3 0z",
  tank_truck:
    "M2 7h11v8H2zm11 3h4l3 3v2h-7zM6 17.5a1.5 1.5 0 113 0 1.5 1.5 0 01-3 0zm10 0a1.5 1.5 0 113 0 1.5 1.5 0 01-3 0z",
  crane_truck:
    "M2 7h11v8H2zm11 3h4l3 3v2h-7zM6 17.5a1.5 1.5 0 113 0 1.5 1.5 0 01-3 0zm10 0a1.5 1.5 0 113 0 1.5 1.5 0 01-3 0z",
  cargo_truck:
    "M2 7h11v8H2zm11 3h4l3 3v2h-7zM6 17.5a1.5 1.5 0 113 0 1.5 1.5 0 01-3 0zm10 0a1.5 1.5 0 113 0 1.5 1.5 0 01-3 0z",
  minibus:
    "M3 7h13v8H3zM16 9h4l2 2v4h-6zM6 17.5a1.5 1.5 0 113 0 1.5 1.5 0 01-3 0zm12 0a1.5 1.5 0 113 0 1.5 1.5 0 01-3 0z",
  bicycle:
    "M5 17.5a2.5 2.5 0 105 0 2.5 2.5 0 00-5 0zm9 0a2.5 2.5 0 105 0 2.5 2.5 0 00-5 0zM7.5 17l5-7h3l1 4M14 8l3 0M9 9h2",
  scooter:
    "M5 17.5a2.5 2.5 0 105 0 2.5 2.5 0 00-5 0zm9 0a2.5 2.5 0 105 0 2.5 2.5 0 00-5 0zM7.5 17l5-7h3l1 4M14 8l3 0M9 9h2",
  tractor:
    "M3 14h6l2-3h4l3 3v3H3zM5 17.5a1.5 1.5 0 113 0 1.5 1.5 0 01-3 0zm10 0a1.5 1.5 0 113 0 1.5 1.5 0 01-3 0z M11 7h4l2 4",
  // GT-VEHICLE-TAXONOMY-OFFROAD-01 — off_road reuses the SUV/4x4
  // placeholder silhouette; trolleybus removed from the taxonomy.
  off_road:
    "M5 12l1.5-4.5A2 2 0 018.4 6h7.2a2 2 0 011.9 1.5L19 12v6a1 1 0 01-1 1h-1a1 1 0 01-1-1v-1H8v1a1 1 0 01-1 1H6a1 1 0 01-1-1v-6z M7 15h2 M15 15h2",
};

function isDarkColor(hex: string): boolean {
  const m = /^#([0-9A-Fa-f]{2})([0-9A-Fa-f]{2})([0-9A-Fa-f]{2})$/.exec(hex);
  if (!m) return false;
  const r = parseInt(m[1]!, 16) / 255;
  const g = parseInt(m[2]!, 16) / 255;
  const b = parseInt(m[3]!, 16) / 255;
  const lum = 0.2126 * r + 0.7152 * g + 0.0722 * b;
  return lum < 0.55;
}

/**
 * Coerce a server-side iconKind string to the closed enum the
 * marker renderer recognises. Returns "generic" for anything
 * unknown so a future server-added icon kind never crashes the
 * admin card. Exported for tests.
 */
export function normalizeIconKind(value: string): VehicleIconKind {
  if ((VEHICLE_ICON_KINDS as readonly string[]).includes(value)) {
    return value as VehicleIconKind;
  }
  return "generic";
}

/**
 * Coerce a server-side operationalStatus string to the closed enum
 * the marker renderer recognises. Returns null for unknown values
 * so the marker omits the ring instead of mislabelling. Exported
 * for tests.
 */
export function normalizeOperationalStatus(value: string): VehicleOperationalStatus | null {
  if ((VEHICLE_OPERATIONAL_STATUSES as readonly string[]).includes(value)) {
    return value as VehicleOperationalStatus;
  }
  return null;
}

/**
 * Validate a server-supplied marker color and fall back to the
 * design-system default brand blue when the value is not the
 * 7-char hex shape the renderer expects. Defends the admin card
 * against any historical row that pre-dates the migration default.
 * Exported for tests.
 */
export function normalizeColor(value: string): string {
  return /^#[0-9A-Fa-f]{6}$/.test(value) ? value : "#0a66c2";
}

function errorMessageFor(
  err: Error,
  t: (k: string, v?: Record<string, string | number>) => string,
): string {
  if (err instanceof ApiError) {
    if (err.status === 403) return t("admin.tenant_360.vehicle_appearance.error.forbidden");
    if (err.status === 404) return t("admin.tenant_360.vehicle_appearance.error.not_found");
  }
  return t("admin.tenant_360.vehicle_appearance.error.generic");
}

const countPillStyle: React.CSSProperties = {
  fontSize: "var(--gt-sys-font-size-xs)",
  fontWeight: "var(--gt-sys-font-weight-semibold)",
  paddingBlock: "var(--gt-sys-space-1)",
  paddingInline: "var(--gt-sys-space-3)",
  borderRadius: "var(--gt-sys-radius-pill, 999px)",
  background: "var(--gt-sys-bg-subtle, transparent)",
  border: "1px solid var(--gt-sys-border)",
  color: "var(--gt-sys-text-muted)",
};

const archivedBadgeStyle: React.CSSProperties = {
  fontSize: "var(--gt-sys-font-size-xs)",
  fontWeight: "var(--gt-sys-font-weight-semibold)",
  paddingBlock: "2px",
  paddingInline: "var(--gt-sys-space-2)",
  borderRadius: "var(--gt-sys-radius-pill, 999px)",
  background: "color-mix(in oklab, var(--gt-sys-status-warning, currentColor) 18%, transparent)",
  color: "var(--gt-sys-status-warning)",
};

const mutedSmallStyle: React.CSSProperties = {
  color: "var(--gt-sys-text-muted)",
  fontSize: "var(--gt-sys-font-size-sm)",
};

const mutedXsStyle: React.CSSProperties = {
  color: "var(--gt-sys-text-muted)",
  fontSize: "var(--gt-sys-font-size-xs)",
};
